import React, { useEffect, useMemo, useState } from 'react';
import { Text } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import type { WorkflowRunStepInstancePayload } from './workflow-run.types';
import { WorkflowRunStatusBadge } from './workflow-run-status';
import { previewValue } from '@cotera/client/app/v1/components/for-each-item-label';

const ROOT_INSTANCE_KEY = 'root';

/**
 * Unwrap server-persisted step input wrappers (`{ t: 'single-parent' | 'fan-in', data }`)
 * so the label shows the actual input value rather than the discriminator string.
 */
function unwrapStepInput(input: unknown): unknown {
  if (
    input !== null &&
    typeof input === 'object' &&
    !Array.isArray(input) &&
    't' in input &&
    'data' in input
  ) {
    const record = input as Record<string, unknown>;
    if (record['t'] === 'single-parent' || record['t'] === 'fan-in') {
      return record['data'];
    }
  }
  return input;
}

export function labelForStepInstance(
  instance: WorkflowRunStepInstancePayload,
  index: number,
  all: readonly WorkflowRunStepInstancePayload[]
): string {
  if (instance.instanceKey === ROOT_INSTANCE_KEY) {
    const childCount = all.filter(
      (row) => row.parentInstanceKey === instance.instanceKey
    ).length;
    return childCount > 0 ? `All items (${childCount})` : 'Root';
  }
  if (instance.parentInstanceKey !== null) {
    const siblings = all.filter(
      (row) => row.parentInstanceKey === instance.parentInstanceKey
    );
    const siblingIndex = siblings.findIndex(
      (row) => row.instanceKey === instance.instanceKey
    );
    const n = siblingIndex >= 0 ? siblingIndex + 1 : index + 1;
    // Prefer the item's input for the label — it identifies *which* item this
    // is — falling back to the output when there is no meaningful input. Use the
    // item's own name as the label when we have one, only falling back to the
    // generic "Item N" when there's nothing meaningful to show.
    const preview =
      previewValue(unwrapStepInput(instance.input)) ??
      previewValue(instance.output);
    return preview !== null ? preview : `Item ${n}`;
  }
  return `Instance ${index + 1}`;
}

export function defaultSelectedInstanceKey(
  instances: readonly WorkflowRunStepInstancePayload[]
): string | null {
  if (instances.length === 0) {
    return null;
  }
  const root = instances.find(
    (instance) => instance.instanceKey === ROOT_INSTANCE_KEY
  );
  const hasChildren = instances.some(
    (instance) => instance.parentInstanceKey !== null
  );
  // For-each parent + children: open on the parent (full items array).
  if (root !== undefined && hasChildren) {
    return root.instanceKey;
  }
  if (root !== undefined && instances.length === 1) {
    return root.instanceKey;
  }
  // Fan-out body nodes often have no root — open the first iteration.
  const firstChild = instances.find(
    (instance) => instance.instanceKey !== ROOT_INSTANCE_KEY
  );
  return firstChild?.instanceKey ?? instances[0]?.instanceKey ?? null;
}

/** Shared instance picker for multi-instance run steps (for-each fan-out). */
export const WorkflowRunStepInstancesList: React.FC<{
  instances: readonly WorkflowRunStepInstancePayload[];
  selectedInstanceKey: string | null;
  onSelect: (instanceKey: string) => void;
}> = ({ instances, selectedInstanceKey, onSelect }) => {
  if (instances.length <= 1) {
    return null;
  }

  const completed = instances.filter(
    (instance) => instance.status === 'completed'
  ).length;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <Text.Caption className="text-muted-foreground block">
          Instances
        </Text.Caption>
        <Text.Caption className="text-muted-foreground block">
          {completed}/{instances.length} completed
        </Text.Caption>
      </div>
      {/* Capped + internally scrollable: the agent step view renders this in a
          shrink-0 header inside an overflow-hidden column, so an uncapped list
          gets clipped and pushes the conversation off-screen. */}
      <ul className="flex max-h-60 flex-col gap-1 overflow-y-auto overscroll-y-contain rounded-md border border-border/60 p-1">
        {instances.map((instance, index) => {
          const selected = instance.instanceKey === selectedInstanceKey;
          return (
            <li key={instance.instanceKey}>
              <button
                type="button"
                onClick={() => onSelect(instance.instanceKey)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                  selected
                    ? 'bg-primary/10 text-foreground'
                    : 'hover:bg-muted/60 text-muted-foreground'
                )}
              >
                <span className="min-w-0 flex-1 truncate font-medium">
                  {labelForStepInstance(instance, index, instances)}
                </span>
                <WorkflowRunStatusBadge status={instance.status} size="sm" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Local selection state for a step's instance list. */
export function useStepInstanceSelection(
  instances: readonly WorkflowRunStepInstancePayload[] | undefined,
  initialInstanceKey?: string
): {
  selectedInstanceKey: string | null;
  setSelectedInstanceKey: (key: string) => void;
  selectedInstance: WorkflowRunStepInstancePayload | null;
} {
  const list = useMemo(() => instances ?? [], [instances]);
  const [selectedInstanceKey, setSelectedInstanceKey] = useState<string | null>(
    () => {
      if (
        initialInstanceKey !== undefined &&
        list.some((instance) => instance.instanceKey === initialInstanceKey)
      ) {
        return initialInstanceKey;
      }
      return defaultSelectedInstanceKey(list);
    }
  );

  useEffect(() => {
    if (
      selectedInstanceKey !== null &&
      list.some((instance) => instance.instanceKey === selectedInstanceKey)
    ) {
      return;
    }
    if (
      initialInstanceKey !== undefined &&
      list.some((instance) => instance.instanceKey === initialInstanceKey)
    ) {
      setSelectedInstanceKey(initialInstanceKey);
      return;
    }
    setSelectedInstanceKey(defaultSelectedInstanceKey(list));
  }, [list, selectedInstanceKey, initialInstanceKey]);

  const selectedInstance =
    list.find((instance) => instance.instanceKey === selectedInstanceKey) ??
    null;

  return {
    selectedInstanceKey,
    setSelectedInstanceKey,
    selectedInstance,
  };
}
